/**
 * Package-store client.
 *
 * At boot, the app calls {@link loadInstalledPackages} which hits
 * `GET /api/packages`, clears any prior `source === 'package'` descriptors
 * ({@link clearPackageNodes}), then registers each kind from the response.
 * Subscriber notifications are coalesced so the UI never observes an empty package
 * registry between clear and register.
 *
 * Package descriptors use the same registry slot as built-ins:
 *
 *   - their `id` is the canonical string `<packageId>/<templateId>@<major>`,
 *   - `source === 'package'`, and
 *   - `package` carries the package-provenance metadata used by the palette to
 *     section Built-in vs PACKAGES.
 *
 * Each kind manifest's `behavior` string is resolved through
 * {@link getBehavior}; `iconRef` through {@link resolveIconRef}. The
 * pre-installed `curio.builtin@1` package rides these registries so its
 * kinds behave identically to the legacy hard-coded descriptors.
 */

import { faCube } from '@fortawesome/free-solid-svg-icons';

import { SupportedType } from '../constants';
import { BUILTIN_PACKAGE_ID } from './packageKeys';
import { isSupportedPortType } from '../constants/supportedPortTypes';
import { getCurrentProjectPackages } from './projectPackagesStore';
import {
  inputOnly,
  outputOnly,
  standardInOut,
  useCodeNodeBehavior,
  usePackageNodeBehavior,
  withBidirectional,
  withPackageStarter,
} from '../adapters/node';
import { packagesApi } from '../services/packages';
import { getToken } from '../utils/authApi';
import {
  getEmbeddedDashboard,
  isStandaloneDashboard,
} from '../standalone/dashboardPayload';

import { getBehavior } from './behaviorRegistry';
import { loadIconRefs, resolveIconRef } from './iconRegistry';
import {
  clearPackageNodes,
  registerNode,
  withSuspendedRegistryNotifications,
} from './nodeRegistry';
import type {
  HandleDef,
  NodeCategory,
  NodeDescriptor,
  PortDef,
} from './types';
import { backendUrl } from '../utils/backendUrl';
import { normalizeWidgets } from '../utils/widgets/widgetModel';

interface RawPackageTemplate {
  id: string; // canonical "<packageId>/<templateId>@<major>"
  templateId: string;
  label: string;
  category: string;
  engine: 'python' | 'javascript';
  description: string;
  icon: string | null;
  iconRef: string | null;
  behavior: string | null;
  paletteOrder: number | null;
  editor: 'code' | 'widgets' | 'grammar' | 'none';
  hasCode: boolean;
  hasWidgets: boolean;
  hasGrammar: boolean;
  grammarId: string | null;
  badge: string | null;
  inputPorts: Array<{ types: string[]; cardinality?: string }>;
  outputPorts: Array<{ types: string[]; cardinality?: string }>;
  /** Optional package-relative path to a single starter source file. */
  source: string | null;
  /** #662: the widgets a freshly dropped node starts with (metadata.widgets shape). */
  widgets?: unknown[] | null;
  bidirectional: boolean;
  containerStyle: {
    nodeWidth?: number;
    nodeHeight?: number;
    noContent?: boolean;
    disablePlay?: boolean;
  } | null;
  hasProvenance: boolean | null;
  /** dev/91: declared backend handler name (sandbox route dispatch). */
  backendHandler?: string | null;
  /** Whether a node of this template loads the dataset it names. */
  loadsDataset?: boolean;
}

interface RawPackage {
  packageId: string;
  major: number;
  version: string;
  name: string;
  publisher: string;
  description: string;
  license: string | null;
  permissions: string[];
  templates: RawPackageTemplate[];
  /** Path (relative to package dir) of a pre-built JS bundle to load before
   *  descriptor build. Registers this package's behavior hooks via
   *  `window.curio.registerBehavior`. See docs/EXTENDING.md §5. */
  behaviorScript?: string;
  /** Server-side dirname (`<packageId>@<major>`). Used for static-asset
   *  URLs like `/api/packages/<dirName>/file/<...>`. */
  dirName?: string;
  /** `model` for a model package, which holds a model's weights. */
  kind?: 'node' | 'model';
  /** A model package's model block, of which the registry keeps the type. */
  model?: { type?: string } | null;
  lineage: {
    forkedFrom: { packageId: string; major: number };
    root: { packageId: string; major: number };
  } | null;
  readOnly?: boolean;
  createdAt?: string;
  createdAtMs?: number | string;
  /** Epoch ms when the package manifest was last written on disk (API diagnostic). */
  installUpdatedAtMs?: number | string;
}

const KNOWN_CATEGORIES: ReadonlySet<NodeCategory> = new Set([
  'data',
  'computation',
  'vis_grammar',
  'vis_simple',
  'flow',
]);

function asCategory(raw: string): NodeCategory {
  return (KNOWN_CATEGORIES.has(raw as NodeCategory) ? raw : 'computation') as NodeCategory;
}

export { BUILTIN_PACKAGE_ID } from './packageKeys';

/**
 * Keep only the types the runtime can actually match.
 *
 * Shares its predicate with the settings dialog's dropdown, so the set a node
 * author can PICK and the set that survives the trip into the registry are one
 * list. They used to be two independent derivations of the same enum, and the
 * dialog's field was free text, so a typo passed the editor and was dropped
 * here in silence (#219).
 */
function asSupportedTypes(raw: string[]): SupportedType[] {
  return raw.filter(isSupportedPortType);
}

function asPortDef(p: { types: string[]; cardinality?: string }): PortDef {
  return {
    types: asSupportedTypes(p.types),
    cardinality: (p.cardinality ?? '1') as PortDef['cardinality'],
  };
}

/** Coerces API numeric-ish fields where JSON may stringify large ints in edge proxies. */
function normalizedEpochMs(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function normalizedInstallUpdatedAtMs(raw: RawPackage['installUpdatedAtMs']): number | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return undefined;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function portCardinalityIconType(ports: PortDef[]): '1' | '2' | 'N' | undefined {
  if (ports.length === 0) return undefined;
  // 'N' wins if any port has unbounded-upper cardinality — range form ("[1,n]", "[0,n]") or bare "n".
  if (ports.some((p) => typeof p.cardinality === 'string' && /^\s*n\s*$|,\s*n\s*]/i.test(p.cardinality))) {
    return 'N';
  }
  return ports.length > 1 ? '2' : '1';
}

function buildDescriptor(pkg: RawPackage, template: RawPackageTemplate, order: number): NodeDescriptor {
  const inputPorts = template.inputPorts.map(asPortDef);
  const outputPorts = template.outputPorts.map(asPortDef);

  const installMsMaybe = normalizedInstallUpdatedAtMs(pkg.installUpdatedAtMs);

  let handles: HandleDef[];
  // A template with no port at all (the Parameter node) takes no edge.
  if (inputPorts.length === 0 && outputPorts.length === 0) handles = [];
  else if (inputPorts.length === 0) handles = outputOnly();
  else if (outputPorts.length === 0) handles = inputOnly();
  else handles = standardInOut();
  if (template.bidirectional) handles = withBidirectional(handles);

  const isBuiltin = pkg.packageId === BUILTIN_PACKAGE_ID;
  const lookedUpBehavior = template.behavior ? getBehavior(template.behavior) : undefined;
  const resolvedBehavior = lookedUpBehavior
    ?? (isBuiltin ? useCodeNodeBehavior : usePackageNodeBehavior);
  // A model package's template without `source` opens with the cell the
  // backend writes for its model's type: a starter named for its template id.
  const defaultCellStarter = !template.source && pkg.kind === 'model' ? template.templateId : undefined;
  // A template that ships a `source` starter (or a default cell) must get it
  // injected on a fresh drop no matter which behavior key its manifest names:
  // resolving `"code"` lands on the no-op `useCodeNodeBehavior`, which would
  // leave the editor empty. `usePackageNodeBehavior` already does injection on
  // its own, so it is not double-wrapped.
  const behavior = (template.source || defaultCellStarter) && resolvedBehavior !== usePackageNodeBehavior
    ? withPackageStarter(resolvedBehavior)
    : resolvedBehavior;
  const icon = resolveIconRef(template.iconRef) ?? faCube;
  const paletteOrder = typeof template.paletteOrder === 'number'
    ? template.paletteOrder
    : 1000 + order; // third-party packages without explicit order sort after built-ins

  // F4: editor === 'none' means no editor surface at all; keeping a truthy
  // config object would cause UniversalNode to mount NodeEditor anyway.
  const adapterEditor = template.editor === 'none'
    ? null
    : { code: template.hasCode, grammar: template.hasGrammar, widgets: template.hasWidgets };

  const container: NodeDescriptor['adapter']['container'] = {
    handleType:
      inputPorts.length === 0
        ? 'out'
        : outputPorts.length === 0
          ? 'in'
          : 'in/out',
    ...(template.containerStyle?.nodeWidth !== undefined ? { nodeWidth: template.containerStyle.nodeWidth } : {}),
    ...(template.containerStyle?.nodeHeight !== undefined ? { nodeHeight: template.containerStyle.nodeHeight } : {}),
    ...(template.containerStyle?.noContent !== undefined ? { noContent: template.containerStyle.noContent } : {}),
    ...(template.containerStyle?.disablePlay !== undefined ? { disablePlay: template.containerStyle.disablePlay } : {}),
  };

  return {
    id: template.id,
    source: 'package',
    package: {
      packageId: pkg.packageId,
      major: pkg.major,
      version: pkg.version,
      name: pkg.name,
      publisher: pkg.publisher,
      source: template.source ?? undefined,
      ...(defaultCellStarter ? { defaultCellStarter } : {}),
      ...(pkg.kind === 'model'
        ? { kind: 'model' as const, ...(pkg.model?.type ? { modelType: pkg.model.type } : {}) }
        : {}),
      ...(pkg.lineage
        ? {
            lineage: {
              forkedFrom: { ...pkg.lineage.forkedFrom },
              root: { ...pkg.lineage.root },
            },
          }
        : {}),
      ...(pkg.readOnly === true ? { readOnly: true } : {}),
      ...(typeof pkg.createdAt === 'string' && pkg.createdAt.trim()
        ? { createdAt: pkg.createdAt.trim() }
        : {}),
      createdAtMs: normalizedEpochMs(pkg.createdAtMs),
      ...(installMsMaybe !== undefined ? { installUpdatedAtMs: installMsMaybe } : {}),
    },
    category: asCategory(template.category),
    label: template.label,
    icon,
    inputPorts,
    outputPorts,
    editor: template.editor,
    inPalette: true,
    paletteOrder,
    description: template.description,
    hasCode: template.hasCode,
    hasWidgets: template.hasWidgets,
    hasGrammar: template.hasGrammar,
    ...(template.hasProvenance !== null ? { hasProvenance: template.hasProvenance } : {}),
    ...(template.backendHandler ? { backendHandler: template.backendHandler } : {}),
    ...(normalizeWidgets(template.widgets).length > 0 ? { widgets: normalizeWidgets(template.widgets) } : {}),
    ...(template.grammarId ? { grammarId: template.grammarId } : {}),
    ...(template.behavior ? { behavior: template.behavior } : {}),
    ...(template.loadsDataset === true ? { loadsDataset: true } : {}),
    ...(template.badge ? { badge: template.badge } : isBuiltin ? {} : { badge: 'PACKAGE' as const }),
    adapter: {
      handles,
      editor: adapterEditor,
      container,
      inputIconType: portCardinalityIconType(inputPorts),
      outputIconType: portCardinalityIconType(outputPorts),
      useNodeBehavior: behavior,
    },
  };
}

/** Every `iconRef` the packages' templates name. */
function templateIconRefs(packages: RawPackage[]): Array<string | null> {
  return packages.flatMap((pkg) => {
    const templates: RawPackageTemplate[] = Array.isArray(pkg.templates) ? pkg.templates : [];
    return templates.map((template) => template.iconRef);
  });
}

/** Internal helper exposed for unit tests. */
export function registerPackageTemplates(packages: RawPackage[]): NodeDescriptor[] {
  const registered: NodeDescriptor[] = [];
  let order = 0;
  for (const pkg of packages) {
    for (const template of pkg.templates) {
      const descriptor = buildDescriptor(pkg, template, order++);
      registerNode(descriptor);
      registered.push(descriptor);
    }
  }
  return registered;
}

/**
 * Fetch installed packages from the backend and register a descriptor per kind.
 *
 * When ``projectFilter`` is provided (a set of dirNames from the current
 * project's lockfile), only packages whose dirName is in the filter — plus
 * the always-on ``curio.builtin`` — are registered. This is how the palette
 * stays project-scoped despite the user-store being shared across projects.
 *
 * When the filter is ``null`` (no project loaded, /catalog page, bootstrap),
 * every installed package is registered.
 *
 * Returns the list of registered descriptors (empty if the user has no
 * packages installed, or if the request fails). Failure is logged and
 * swallowed so app boot never blocks on package discovery.
 */
/**
 * Loads a package's pre-built `behaviorScript` bundle by injecting a
 * <script> tag pointed at `/api/packages/<dirName>/file/<script>`. The
 * bundle's top-level side-effect calls `window.curio.registerBehavior`
 * for each behavior hook it ships, so by the time the returned Promise
 * resolves the behavior keys declared in the package's templates are
 * registered and `buildDescriptor`'s lookup will succeed.
 *
 * Errors are swallowed (and logged) — one broken package's bundle
 * shouldn't take the whole canvas down. The descriptors for its
 * templates will fall back to `usePackageNodeBehavior` (generic code
 * editor) so the palette still renders.
 */
/**
 * Bundles currently being fetched, keyed by `<packageId>@<major>`.
 *
 * The DOM marker below is only written *after* two awaits, so it cannot stop a
 * second caller that entered the window in between — and nothing single-flights
 * `refreshPackageRegistry`, which several surfaces call on the same tick. The
 * result was the same bundle fetched and evaluated repeatedly on one page load,
 * each pass re-running its top-level `registerBehavior` side-effects and
 * logging `Behavior "x" already registered; overwriting.` This map is claimed
 * before the first await, so concurrent callers join the in-flight load instead
 * of starting their own.
 */
const inFlightBehaviorScripts = new Map<string, Promise<void>>();

async function loadPackageBehaviorScripts(packages: RawPackage[]): Promise<void> {
  // A standalone dashboard was served with these scripts rather than a URL to
  // fetch them from. Run them the same way the fetched ones are run, as inline
  // script text that self-registers through `window.curio.registerBehavior`, so
  // a package tile behaves on a published page exactly as it does on a canvas.
  const embeddedScripts = getEmbeddedDashboard()?.registry?.behaviorScripts;
  if (embeddedScripts) {
    for (const [key, text] of Object.entries(embeddedScripts)) {
      if (document.querySelector(`script[data-curio-package="${key}"]`)) continue;
      const el = document.createElement("script");
      el.dataset.curioPackage = key;
      el.text = String(text);
      document.head.appendChild(el);
    }
    return;
  }
  // A page with no payload at all still fetches; a payload that simply has no
  // scripts has nothing to run. Either way nothing below should call out.
  if (isStandaloneDashboard()) return;
  const base = backendUrl();
  const targets = packages.filter((p) => p.behaviorScript && p.dirName);
  if (targets.length === 0) return;
  const token = getToken();
  // Sequential, not Promise.all, so each bundle's top-level
  // `registerBehavior` side-effect runs before the next bundle's does —
  // matches the deterministic order the old `<script async={false}>` chain
  // guaranteed.
  for (const p of targets) {
    const key = `${p.packageId}@${p.major}`;
    // De-dupe across re-renders: the same bundle should only load once
    // even if refreshPackageRegistry runs again.
    const existing = document.querySelector(`script[data-curio-package="${key}"]`);
    if (existing) continue;
    // De-dupe *within* a tick: join a load already in flight rather than
    // racing it to the same <script> injection.
    const pending = inFlightBehaviorScripts.get(key);
    if (pending) {
      await pending;
      continue;
    }
    const url = `${base}/api/packages/${encodeURIComponent(p.dirName!)}/file/${p.behaviorScript}`;
    let settle: () => void = () => {};
    inFlightBehaviorScripts.set(key, new Promise<void>((resolve) => { settle = resolve; }));
    try {
      // ``<script src>`` cannot carry an Authorization header, and the
      // file-serving endpoint is under ``@require_auth``. Firefox's ORB
      // then blocks the resulting 401 response and the bundle never
      // evaluates. Fetch via ``fetch`` (with the Bearer token) and
      // inject the body as inline script text instead.
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      // ``cache: 'no-store'`` bypasses the HTTP cache entirely. Without it,
      // a stale bundle (e.g. an earlier broken build still in the disk
      // cache) gets re-evaluated forever — the file-serving endpoint
      // doesn't set strong cache headers, so the browser uses its
      // heuristic freshness window. Package bundles are small + fetched
      // once per boot, so the cost of bypassing the cache is negligible
      // next to the correctness win.
      const res = await fetch(url, { headers, cache: 'no-store' });
      if (!res.ok) {
        console.warn(
          `[curio] failed to load behavior script for ${key}: HTTP ${res.status}`,
        );
        continue;
      }
      const code = await res.text();
      const s = document.createElement('script');
      s.dataset.curioPackage = key;
      s.textContent = code;
      document.head.appendChild(s);
    } catch (err) {
      console.warn(`[curio] failed to load behavior script for ${key}:`, err);
      // never throw — descriptor build still proceeds (with code-editor fallback)
    } finally {
      inFlightBehaviorScripts.delete(key);
      settle();
    }
  }
}

/**
 * Register every package installed for this account.
 *
 * Deliberately unfiltered. The per-dataflow scope is applied when the palette is
 * READ (``getPaletteNodeTypes``), not when descriptors are registered: baking it
 * in here meant the registry held one dataflow's view, so every scope change
 * needed a refetch to correct it and any path that forgot one left the previous
 * dataflow's packages on the palette (#204, #220).
 *
 * Registering everything also means a canvas node whose package is not in this
 * dataflow's lockfile still resolves its descriptor instead of rendering as an
 * unknown kind.
 *
 * DESCRIPTORS are unfiltered; behavior SCRIPTS are not. Building a descriptor is
 * local work, but each behavior bundle is a fetch plus a ``<script>`` injection,
 * and awaiting all of them on every refresh made the whole registry refresh
 * scale with the size of the account rather than of the open dataflow. Those
 * are loaded for the dataflow's own packages, and lazily for anything else.
 */
/**
 * Orders overlapping {@link loadInstalledPackages} calls.
 *
 * Each call replaces every package node in the registry, and it does so after
 * two awaits (the installed list, then behavior scripts). Several surfaces
 * refresh on the same tick, so calls overlap, and without this the one that
 * FINISHED last won. An import's refresh could land, then a boot refresh
 * holding a snapshot from before the import could land after it and take the
 * package back out of the palette, where it stayed until a page reload. The
 * mirror image resurrected packages that had just been uninstalled.
 *
 * The rule is "never apply an answer older than one already applied", not
 * "only the newest call may apply". The difference is a newest call that
 * FAILS: under the stricter rule an older call's perfectly good answer would be
 * thrown away too, and the registry would not update at all.
 *
 * ``projectPackagesStore`` guards its own version of this race the same way
 * (``applyProjectLockfile`` and its revision counter).
 */
let startedLoads = 0;
let appliedLoad = 0;

export async function loadInstalledPackages(): Promise<NodeDescriptor[]> {
  const load = ++startedLoads;
  try {
    // A standalone dashboard was served with the descriptors inside it. Taken
    // here rather than further down so everything after this point is the
    // ordinary path: the same scoping, the same descriptor build, the same
    // registry replace. Curio bundles node implementations but not node
    // descriptors, so without these a page with every row it needs still shows
    // "Loading node..." on every tile. A page that carries none, such as one
    // the server refused to build, has none: it never asks the server.
    const embedded = getEmbeddedDashboard();
    const { packages } = embedded
      ? { packages: embedded.registry?.packages ?? [] }
      : await packagesApi.listInstalled();
    const filtered = packages ?? [];
    const scope = getCurrentProjectPackages();
    const scoped =
      scope === null
        ? filtered
        : filtered.filter(
            (p) =>
              p.packageId === BUILTIN_PACKAGE_ID ||
              scope.has(`${p.packageId}@${p.major}`),
          );
    // Every template gets a descriptor, so every template's icon is loaded
    // before descriptors are built: a palette tile never draws a cube first.
    // Started here so the icons load while the behavior scripts do.
    const icons = loadIconRefs(templateIconRefs(filtered));
    // Inject and await any `behaviorScript` bundles BEFORE descriptor
    // build so the behavior keys referenced in the templates are
    // actually registered against the global behavior registry. Without
    // this step, `getBehavior()` returns undefined and packages with
    // custom behaviors soft-fail to the package code editor.
    await loadPackageBehaviorScripts(scoped);
    if (icons) await icons;
    // A call that started later has already put its answer in the registry, so
    // this one is out of date. Checked here, after the last await, because the
    // replace below is synchronous: nothing can slip in between the two.
    // Nobody reads the return value when superseded (``refreshPackageRegistry``
    // only waits on it), and an empty list matches the error path below.
    if (load < appliedLoad) return [];
    appliedLoad = load;
    // Replace package-derived kinds wholesale — `registerNode` only adds/overwrites,
    // so without this pass, uninstalled packages would leave stale palette entries.
    // Notify subscribers only after clear + register so React Flow keeps package node types wired.
    return withSuspendedRegistryNotifications(() => {
      clearPackageNodes();
      return registerPackageTemplates(filtered);
    });
  } catch (error) {
    const status = (error as { status?: number }).status;
    if (status !== 401) {
      console.warn('Failed to load installed packages:', error);
    }
    return [];
  }
}
